// The phone's answers on their way (Poulpe-624): each question's answer is
// sent exactly once.
//
// - An answer is named by one mutation id, chosen when it is first sent and
//   kept for its question, version and replies. A second tap while it is
//   on its way sends nothing. A retry after a failure whose outcome is
//   unknown sends the same id, which the server replays without answering
//   twice (crates/poulpe-server/src/mutations.rs).
// - Offline, the answer is kept here, in memory alone, while the app stays
//   open: nothing is written to the device, and the store's durable outbox
//   is not used (the N6 lead, 4 Nov). Once the store is back online, and has
//   read the feed, the answer goes, with the same id, if its question is
//   still open at the version it answers; otherwise it is dropped, unsent,
//   and the question's screen says so and shows the question as it now
//   stands.
// - A failure that leaves the store offline keeps the answer for the
//   reconnection; any other is said, and the person may try again.
// - What was picked and not sent is kept here too, in memory, for each
//   question's version: back from a session's facts, the question shows it.
// - The keeper is the signed-in person's: App.tsx draws it above the choice
//   of layout, so turning the phone or opening the full board loses no
//   answer, and draws a new one when who is signed in changes, so no answer
//   outlives its person.
// - Offline is the store's word, read when the answer goes, not a render's:
//   given its mutation id, the store sends nothing offline and queues
//   nothing in its durable outbox, and the answer stays kept here.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { Question, Reply } from '../api/types'
import type { Pick } from '../questions/QuestionCard'
import { uuid4 } from '../api/uuid'
import { useBoardState, useStore } from '../store/context'

/** An answer not yet taken: kept for the connection, on its way, failed, or dropped because its question changed or left before it went. */
export interface Kept {
  question: string
  version: number
  replies: Reply[]
  mutationId: string
  phase: 'kept' | 'sending' | 'failed' | 'dropped'
  /** Why it failed, in the server's words. */
  error?: string
}

/** What sending did: taken, kept for the connection, refused because the question changed, failed, or nothing (already on its way). */
export type SendOutcome = 'taken' | 'kept' | 'stale' | 'failed' | 'busy'

interface Keeper {
  kept: ReadonlyMap<string, Kept>
  send(question: Question, replies: Reply[]): Promise<SendOutcome>
  /** What was picked for a question's version and not sent yet, kept while the app is open: a screen left for the session's facts comes back as it was. */
  draft(question: string, version: number): readonly Pick[] | null
  keepDraft(question: string, version: number, picks: readonly Pick[]): void
}

const KeeperContext = createContext<Keeper | null>(null)

const sameReplies = (a: Reply[], b: Reply[]) => JSON.stringify(a) === JSON.stringify(b)

export function AnswerKeeper({ children }: { children: ReactNode }) {
  const store = useStore()
  const { offline, questions } = useBoardState()
  const [kept, setKept] = useState<ReadonlyMap<string, Kept>>(new Map())
  /** What is kept, as of now: the state lags a render behind. */
  const held = useRef(new Map<string, Kept>())
  /** The questions whose answer is on its way: a second send waits for none. */
  const flying = useRef(new Set<string>())
  const drafts = useRef(new Map<string, { version: number; picks: readonly Pick[] }>())
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  const put = useCallback((id: string, entry: Kept | null) => {
    if (entry) held.current.set(id, entry)
    else held.current.delete(id)
    if (alive.current) setKept(new Map(held.current))
  }, [])

  const deliver = useCallback(async (question: Question, entry: Kept): Promise<SendOutcome> => {
    flying.current.add(question.id)
    put(question.id, { ...entry, phase: 'sending', error: undefined })
    try {
      const outcome = await store.answer(question, entry.replies, undefined, entry.mutationId)
      if (outcome.ok) { put(question.id, null); return 'taken' }
      if (outcome.stale) { put(question.id, { ...entry, phase: 'dropped' }); return 'stale' }
      // Offline before it went, or lost on the way: kept, for the reconnection.
      if (outcome.offline || store.getState().offline) { put(question.id, { ...entry, phase: 'kept' }); return 'kept' }
      put(question.id, { ...entry, phase: 'failed', error: outcome.message })
      return 'failed'
    } finally {
      flying.current.delete(question.id)
    }
  }, [store, put])

  const send = useCallback(async (question: Question, replies: Reply[]): Promise<SendOutcome> => {
    if (flying.current.has(question.id)) return 'busy'
    const before = held.current.get(question.id)
    // The same answer again keeps its id: a retry is a replay, never a second answer.
    const mutationId = before && before.version === question.version && sameReplies(before.replies, replies) ? before.mutationId : uuid4()
    const entry: Kept = { question: question.id, version: question.version, replies, mutationId, phase: 'kept' }
    if (store.getState().offline) { put(question.id, entry); return 'kept' }
    return deliver(question, entry)
  }, [store, put, deliver])

  // Back online, with the feed read: what was kept goes, if its question still asks it.
  useEffect(() => {
    // The store, not this render, says whether it is online: it may have gone offline again since.
    if (offline || store.getState().offline) return
    for (const entry of held.current.values()) {
      if (entry.phase !== 'kept' || flying.current.has(entry.question)) continue
      const question = questions.get(entry.question)
      if (question?.state === 'open' && question.version === entry.version) void deliver(question, entry)
      else put(entry.question, { ...entry, phase: 'dropped' })
    }
  }, [offline, questions, deliver, put, store])

  const draft = useCallback((question: string, version: number) => {
    const held = drafts.current.get(question)
    return held && held.version === version ? held.picks : null
  }, [])
  const keepDraft = useCallback((question: string, version: number, picks: readonly Pick[]) => { drafts.current.set(question, { version, picks }) }, [])
  const value = useMemo(() => ({ kept, send, draft, keepDraft }), [kept, send, draft, keepDraft])
  return <KeeperContext.Provider value={value}>{children}</KeeperContext.Provider>
}

export function useKeeper(): Keeper {
  const keeper = useContext(KeeperContext)
  if (!keeper) throw new Error('useKeeper needs an AnswerKeeper')
  return keeper
}
